import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Shield, ChevronRight, Star, Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getProductBySlug, PRODUCTS } from '../data/products';
import { fetchProductBySlug } from '../services/api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { slug } = useParams();
  const staticProduct = getProductBySlug(slug);
  const [product, setProduct] = useState(staticProduct || null);
  const { addToCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [allowBackorder, setAllowBackorder] = useState(false);

  useEffect(() => {
    fetchProductBySlug(slug)
      .then(p => { if (p) setProduct(p); })
      .catch(() => {});
    // Fetch store settings to check backorder toggle
    fetch(`${BASE}/api/store-settings`)
      .then(r => r.ok ? r.json() : {})
      .then(s => setAllowBackorder(s.allow_backorder || false))
      .catch(() => {});
  }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="font-bebas text-4xl text-zinc-600 tracking-wider mb-4">PRODUCT NOT FOUND</p>
          <Link to="/shop" className="font-bebas text-lg tracking-widest text-orange-500 hover:text-white transition-colors">
            RETURN TO SHOP →
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity, selectedVariant);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const relatedProducts = PRODUCTS.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const faqs = [
    { q: 'Does this require an FFL transfer?', a: product.nfaItem ? 'Yes. All suppressors are NFA items requiring ATF Form 4 transfer through a licensed FFL/SOT dealer. An ATF approval period applies.' : 'No. This product is not an NFA item and ships directly to you.' },
    { q: 'What is the lead time?', a: 'Most in-stock items ship within 5–10 business days. Custom-machined items (clamp-ons, threaded brakes) are made to order and may require 2–3 weeks. You will receive a confirmation email when your order ships.' },
    { q: 'What finishes are available?', a: 'Products are available in natural stainless, Cerakote graphite black, and FDE (Flat Dark Earth) Cerakote. Select at checkout or specify in your order notes.' },
    { q: 'Do you ship internationally?', a: 'We ship to most locations that USPS serves, including Canada, Australia, France, and Sweden. However, NFA items (suppressors) may only ship within the United States to licensed FFL/SOT dealers.' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      {/* Breadcrumb */}
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-zinc-600">
          <Link to="/" className="font-inter text-xs hover:text-zinc-400 transition-colors">Home</Link>
          <ChevronRight size={12} />
          <Link to="/shop" className="font-inter text-xs hover:text-zinc-400 transition-colors">Shop</Link>
          <ChevronRight size={12} />
          <Link to={`/${product.category === 'sme' ? 'sound-mitigation-equipment' : product.category}`} className="font-inter text-xs hover:text-zinc-400 transition-colors capitalize">
            {product.category.replace('-', ' ')}
          </Link>
          <ChevronRight size={12} />
          <span className="font-inter text-xs text-zinc-400 truncate">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* === IMAGE GALLERY === */}
          <div>
            {/* Main image */}
            <div
              data-testid="product-main-image"
              className="bg-[#141414] border border-white/10 aspect-square overflow-hidden mb-3"
            >
              <img
                src={product.images[selectedImage] || product.image}
                alt={product.name}
                className="w-full h-full object-contain p-8"
                onError={e => {
                  e.target.parentElement.classList.add('img-placeholder');
                  e.target.style.opacity = '0.2';
                }}
              />
            </div>
            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`thumbnail-${i}`}
                    onClick={() => setSelectedImage(i)}
                    className={`w-16 h-16 bg-[#141414] border overflow-hidden transition-colors ${
                      selectedImage === i ? 'border-orange-500' : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain p-1" onError={e => { e.target.style.opacity = '0.3'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* === PRODUCT INFO === */}
          <div>
            {/* NFA badge */}
            {product.nfaItem && (
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center gap-1.5 font-inter text-xs font-semibold text-orange-400 bg-orange-500/10 border border-orange-500/30 px-3 py-1.5">
                  <Shield size={12} /> NFA Item — All NFA Rules Apply
                </span>
              </div>
            )}
            {!product.nfaItem && (
              <div className="flex items-center gap-2 mb-4">
                <span className="font-inter text-xs font-semibold text-green-400 bg-green-500/10 border border-green-500/30 px-3 py-1.5">
                  No NFA Registration Required
                </span>
              </div>
            )}

            {/* Badge */}
            {product.badge && (
              <span className="inline-block font-bebas text-xs tracking-widest bg-orange-500 text-white px-2 py-1 mb-3">
                {product.badge}
              </span>
            )}

            {/* Name */}
            <h1 data-testid="product-name" className="font-bebas text-4xl lg:text-5xl text-white tracking-wider leading-tight mb-1">
              {product.name}
            </h1>
            {product.subtitle && (
              <p className="font-inter text-sm text-zinc-500 mb-4">{product.subtitle}</p>
            )}

            {/* Rating */}
            <div className="flex items-center gap-2 mb-5">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.floor(product.rating) ? 'text-orange-500 fill-orange-500' : 'text-zinc-700'} />
                ))}
              </div>
              <span className="font-inter text-sm text-zinc-400">{product.rating} ({product.reviewCount} reviews)</span>
            </div>

            {/* Price */}
            <div data-testid="product-detail-price" className="mb-5">
              {product.priceRange ? (
                <span className="font-bebas text-4xl text-white tracking-wide">{product.priceRange}</span>
              ) : (
                <span className="font-bebas text-4xl text-white tracking-wide">${product.price.toFixed(2)}</span>
              )}
            </div>

            {/* Short desc */}
            <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-6 border-l-2 border-orange-500 pl-4">
              {product.shortDesc}
            </p>

            {/* Variants */}
            {product.variants && (
              <div className="mb-6">
                <p className="font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">Finish</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map(v => (
                    <button
                      key={v}
                      data-testid={`variant-${v.toLowerCase().replace(' ', '-')}`}
                      onClick={() => setSelectedVariant(v)}
                      className={`font-inter text-sm px-4 py-2 border transition-colors ${
                        selectedVariant === v
                          ? 'border-orange-500 bg-orange-500/10 text-orange-400'
                          : 'border-white/20 text-zinc-400 hover:border-white/40'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-white/20">
                <button
                  data-testid="qty-decrease"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors font-inter text-sm"
                >
                  −
                </button>
                <span data-testid="qty-value" className="px-5 py-3 font-inter text-base text-white border-x border-white/20">
                  {quantity}
                </span>
                <button
                  data-testid="qty-increase"
                  onClick={() => setQuantity(q => q + 1)}
                  className="px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors font-inter text-sm"
                >
                  +
                </button>
              </div>
              <span className={`relative group font-inter text-xs uppercase tracking-wider ${
                product.inStock ? 'text-green-500' : allowBackorder ? 'text-orange-400 cursor-help' : 'text-zinc-600'
              }`}>
                {product.inStock ? 'In Stock' : allowBackorder ? (
                  <>
                    Built to Order
                    {/* Hover tooltip */}
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 bg-[#1a1a1a] border border-orange-500/30 text-zinc-300 font-inter text-[11px] normal-case tracking-normal leading-relaxed px-3 py-2.5 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-xl whitespace-normal">
                      This item is <strong className="text-white">built to order.</strong> Your purchase queues production at our Texas facility in the order we receive it. We'll notify you when your suppressor enters production and again when it ships to your FFL dealer.
                      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-orange-500/30" />
                    </span>
                  </>
                ) : 'Out of Stock'}
              </span>
            </div>

            {/* Add to Cart */}
            <button
              data-testid="add-to-cart-button"
              onClick={handleAddToCart}
              disabled={!product.inStock && !allowBackorder}
              className={`w-full flex items-center justify-center gap-3 font-bebas text-lg tracking-widest py-4 transition-all mb-3 ${
                addedToCart
                  ? 'bg-green-600 text-white'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              } ${(!product.inStock && !allowBackorder) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {addedToCart ? (
                <><Check size={20} />ADDED TO CART!</>
              ) : !product.inStock && allowBackorder ? (
                <><ShoppingCart size={20} />BUILT TO ORDER — ADD TO CART</>
              ) : (
                <><ShoppingCart size={20} />ADD TO CART</>
              )}
            </button>

            {/* NFA shipping note */}
            {product.nfaItem && (
              <div className="flex items-start gap-2 bg-[#1a1a1a] border border-orange-500/20 p-3 mb-5">
                <AlertCircle size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
                <p className="font-inter text-xs text-zinc-400">
                  NFA Item: Must choose UPS shipping. All federal NFA regulations apply. Transfer through licensed FFL/SOT dealer required.
                </p>
              </div>
            )}

            {/* Use cases */}
            {product.useCases && (
              <div className="mb-5">
                <p className="font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">Ideal For</p>
                <div className="flex flex-wrap gap-2">
                  {product.useCases.map(uc => (
                    <span key={uc} className="font-inter text-xs text-zinc-300 bg-white/5 border border-white/10 px-3 py-1">
                      {uc}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* === SPECS & FEATURES === */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Specs */}
          <div className="bg-[#111111] border border-white/10">
            <div className="border-b border-white/10 px-6 py-4">
              <h2 className="font-bebas text-2xl text-white tracking-wider">SPECIFICATIONS</h2>
            </div>
            <div className="p-6">
              <table className="w-full" data-testid="specs-table">
                <tbody>
                  {Object.entries(product.specs).map(([key, val]) => (
                    <tr key={key} className="border-b border-white/5 last:border-0">
                      <td className="py-3 pr-4 font-inter text-xs text-zinc-500 uppercase tracking-wider w-1/2">{key}</td>
                      <td className="py-3 font-inter text-sm text-white">{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Features */}
          <div className="bg-[#111111] border border-white/10">
            <div className="border-b border-white/10 px-6 py-4">
              <h2 className="font-bebas text-2xl text-white tracking-wider">FEATURES</h2>
            </div>
            <div className="p-6 space-y-3">
              {product.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-4 h-4 bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={10} className="text-orange-500" />
                  </div>
                  <span className="font-inter text-sm text-zinc-300">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* === FULL DESCRIPTION === */}
        <div className="mt-6 bg-[#111111] border border-white/10 p-8">
          <h2 className="font-bebas text-2xl text-white tracking-wider mb-4">PRODUCT DETAILS</h2>
          <p className="font-inter text-sm text-zinc-400 leading-relaxed">{product.description}</p>
        </div>

        {/* === FAQ === */}
        <div className="mt-6 bg-[#111111] border border-white/10">
          <div className="border-b border-white/10 px-6 py-4">
            <h2 className="font-bebas text-2xl text-white tracking-wider">FREQUENTLY ASKED QUESTIONS</h2>
          </div>
          <div data-testid="product-faq">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-white/5 last:border-0">
                <button
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/3 transition-colors"
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                >
                  <span className="font-inter text-sm font-medium text-white">{faq.q}</span>
                  {faqOpen === i ? (
                    <ChevronUp size={16} className="text-zinc-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-zinc-400 flex-shrink-0" />
                  )}
                </button>
                {faqOpen === i && (
                  <div className="px-6 pb-5">
                    <p className="font-inter text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* === RELATED PRODUCTS === */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bebas text-3xl text-white tracking-wider">RELATED PRODUCTS</h2>
              <Link to={`/${product.category === 'sme' ? 'sound-mitigation-equipment' : product.category}`} className="font-bebas text-sm text-orange-500 hover:text-white transition-colors tracking-wider">
                VIEW ALL →
              </Link>
            </div>
            <div data-testid="related-products" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedProducts.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
